import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getToken } from "../../utils/AdalConfig";
import { VarianceService } from "../../utils/ServiceLocator";
import { IndividualSaveCommand, VarianceStartCommand, VarianceStatusPostCommand, VarianceWorklistRequest, VarianceWorklistSearchRequest } from "./commands";
import { Individual, VarianceRequest, Worklist } from "./model";
import { VariancePostCommand } from "./commands/VariancePostCommand";
import { VarianceApprovalPostCommand } from "./commands/VarianceApprovalPostCommand";
import { VarianceApprovalPostResult } from "./commands/VarianceApprovalPostResult";
import { VarianceRejectPostResult } from "./commands/VarianceRejectPostResult";
import { VarianceRejectPostCommand } from "./commands/VarianceRejectPostCommand";
import { IncidentCommentAddCommand } from "./commands/IncidentCommentAddCommand";
import { IncidentCommentDeleteCommand } from "./commands/IncidentCommentDeleteCommand";
import { IncidentCommentEditCommand } from "./commands/IncidentCommentEditCommand";
import { IncidentComment } from "./model/varianceRequest/IncidentComment";
import { VariancePendingPostResult } from "./commands/VariancePendingPostResult";
import { VariancePendingPostCommand } from "./commands/VariancePendingPostCommand";
import { VarianceCompletePostCommand } from "./commands/VarianceCompletePostCommand";
import { VarianceCompletePostResult } from "./commands/VarianceCompletePostResult";
import { IncidentWitnessAddCommand } from "./commands/WitnessAddCommand";
import { IncidentWitnessRemoveCommand } from "./commands/WitnessRemoveCommand";
import { IncidentAssignUserCommand } from "./commands/IncidentAssignUserCommand";
import { VarianceMyWorklistRequest } from "./commands/VarianceMyWorklistRequest";
import { VariancePendingApprovalPostCommand } from "./commands/VariancePendingApprovalPostCommand";
import { VariancePendingApprovalPostResult } from "./commands/VariancePendingApprovalPostResult";
import { VarianceDocument } from "./model/varianceRequest/VarianceDocuments";
import { VarianceDocRemoveCommand } from "./commands/VarianceDocRemoveCommand";
import { IncidentAssignUsersCommand } from "./commands/IncidentAssignUsersCommand";
import { VarianceReopenPostCommand } from "./commands/VarianceReopenPostCommand";
import { VarianceReopenPostResult } from "./commands/VarianceReopenPostResult";
import { DirectorResponseItem } from "./model/varianceRequest/DirectorResponseItem";
import { DirectorResponseAddCommand } from "./commands/DirectorResponseAddCommand";
import { DirectorResponseEditCommand } from "./commands/DirectorResponseEditCommand";
import { DirectorResponseDeleteCommand } from "./commands/DirectorResponseDeleteCommand";

export const varianceApi = createApi({
    reducerPath: 'varianceApi',
    baseQuery: fetchBaseQuery({
        baseUrl: VarianceService,
        prepareHeaders: (headers) => {
            const token = getToken();
            headers.set('Authorization', 'Bearer ' + token);
            headers.set('content-type', 'application/json')
            return headers;
        }
    }),
    tagTypes: ['variance', 'individual', 'worklist', 'search'],
    endpoints: (builder) => ({
        startPatientVariance: builder.mutation<VarianceRequest, VarianceStartCommand>({
            query: (body) => {
                return { url: 'VarianceStart', method: 'POST', body: body, }
            },
        }),
        IndividualSaveCommand: builder.mutation<VarianceRequest, IndividualSaveCommand>({
            query:(body:IndividualSaveCommand)=>({url:'Individual',method:('POST'),body,}),
            invalidatesTags:['individual']
        }),
        findWorklist: builder.query<Worklist[], VarianceWorklistRequest>({
            query: (request: VarianceWorklistRequest) => 'varianceWorklist?center='+ request.center + '&user='  + request.user + '&queryParams=' + request.queryParam + '&isAdmin=' + request.isAdmin,
            providesTags: ['worklist']
        }),
        myWorklist: builder.query<Worklist[], VarianceMyWorklistRequest>({
            query: (request: VarianceMyWorklistRequest) => 'varianceMyWorklist?center=' + request.center + '&user=' + request.user + '&isAdmin=' + request.isAdmin,
            providesTags: ['worklist']
        }),
        findVarianceRequest: builder.query<VarianceRequest, string>({
            query: (id: string) => 'VarianceRequestDetail?Id=' + id,
            providesTags: ['variance']
        }),
        varianceWorklistSearch: builder.query<Worklist[], VarianceWorklistSearchRequest>({
            query: (request: VarianceWorklistSearchRequest) => 'search?center=' + request.center + '&searchCriteria=' + request.searchCriteria,
            providesTags: ['search']
        }),
        varianceSave: builder.mutation<VarianceRequest, VariancePostCommand>({
            query: (body) => {
                return { url: 'Variance', method: 'POST', body: body, }
            },
            invalidatesTags: ['variance', 'worklist']
        }),
        varianceStatusUpdate: builder.mutation<VarianceRequest, VarianceStatusPostCommand>({
            query: (body) => {
                return { url: 'VarianceStatusUpdate', method: 'POST', body: body, }
            }, invalidatesTags: ['worklist']
        }),
        varianceApprove: builder.mutation<VarianceApprovalPostResult, VarianceApprovalPostCommand>({
            query: (body) => {
                return { url: 'VarianceApproval', method: 'POST', body: body, }
            },
            invalidatesTags: ['variance', 'worklist']
        }),
        variancePending: builder.mutation<VariancePendingPostResult, VariancePendingPostCommand>({
            query: (body) => {
                return { url: 'VariancePending', method: 'POST', body: body, }
            },
            invalidatesTags: ['variance', 'worklist']
        }),
        variancePendingApprove: builder.mutation<VariancePendingApprovalPostResult, VariancePendingApprovalPostCommand>({
            query: (body) => {
                return { url: 'VariancePendingApproval', method: 'POST', body: body, }
            },
            invalidatesTags: ['variance', 'worklist']
        }),
        varianceReject: builder.mutation<VarianceRejectPostResult, VarianceRejectPostCommand>({
            query: (body) => {
                return { url: 'VarianceReject', method: 'POST', body: body, }
            },
            invalidatesTags: ['variance', 'worklist']
        }),
        varianceComplete: builder.mutation<VarianceCompletePostResult, VarianceCompletePostCommand>({
            query: (body) => {
                return { url: 'VarianceComplete', method: 'POST', body: body, }
            },
            invalidatesTags: ['variance', 'worklist']
        }),
        incidentCommentAdd:builder.mutation<IncidentComment,IncidentCommentAddCommand>({
            query:(body:IncidentCommentAddCommand) => {
                return{url:'CommentAdd',method:'POST',body:body}
            },
            invalidatesTags:['variance']
        }),
        incidentCommentEdit:builder.mutation<IncidentComment,IncidentCommentEditCommand>({
            query:(body:IncidentCommentEditCommand) => {
                return{url:'CommentEdit',method:'PUT',body:body}
            },
            invalidatesTags:['variance']
        }),
        incidentCommentDelete:builder.mutation<void,IncidentCommentDeleteCommand>({
            query:(body:IncidentCommentDeleteCommand) => {
                return{url:`CommentDelete/${body.CommentId}?VarianceId=${body.VarianceId}`,method:'DELETE'}
            },
            invalidatesTags:['variance']
        }),
        incidentWitnessAdd:builder.mutation<Individual,IncidentWitnessAddCommand>({
            query:(body:IncidentWitnessAddCommand)=>({url:'WitnessAdd',method:('POST'),body,}),
            invalidatesTags:['variance']
        }),
        incidentWitnessRemove:builder.mutation<Individual, IncidentWitnessRemoveCommand>({
            query:(body:IncidentWitnessRemoveCommand)=>({url:'WitnessRemove',method:('POST'),body,}),
            invalidatesTags:['variance']
        }),
        incidentUnAssignUser:builder.mutation<VarianceRequest, IncidentAssignUserCommand>({
            query:(body:IncidentAssignUserCommand)=>({url:'UnAssignUser',method:('POST'),body,}),
            invalidatesTags:['variance']
        }),
        incidentAssignUsers:builder.mutation<VarianceRequest, IncidentAssignUsersCommand>({
            query:(body:IncidentAssignUsersCommand)=>({url:'AssignUsers',method:('POST'),body,}),
            invalidatesTags:['variance']
        }),
        documentRemove:builder.mutation<VarianceDocument,VarianceDocRemoveCommand>({
            query:(body) =>{            
                return {url:'DocumentRemove', method:'POST', body:body,}
            }
        }),
        varianceReopen: builder.mutation<VarianceReopenPostResult, VarianceReopenPostCommand>({
            query: (body) => {
                return { url: 'VarianceReopen', method: 'POST', body: body, }
            },
            invalidatesTags: ['variance', 'worklist']
        }),
        directorResponseAdd: builder.mutation<DirectorResponseItem, DirectorResponseAddCommand>({
            query: (body) => {
                return { url: 'DirectorResponseAdd', method: 'POST', body: body }
            },
            invalidatesTags: ['variance']
        }),
        directorResponseEdit: builder.mutation<DirectorResponseItem, DirectorResponseEditCommand>({
            query: (body) => {
                return { url: 'DirectorResponseEdit', method: 'PUT', body: body }
            },
            invalidatesTags: ['variance']
        }),
        directorResponseDelete: builder.mutation<void, DirectorResponseDeleteCommand>({
            query: (body) => {
                return { url: `DirectorResponseDelete/${body.DirectorResponseId}?VarianceId=${body.VarianceId}`, method: 'DELETE' }
            },
            invalidatesTags: ['variance']
        }),

    })
});

export const { useStartPatientVarianceMutation, useFindWorklistQuery, useFindVarianceRequestQuery, useVarianceSaveMutation,
    useVarianceStatusUpdateMutation, useVarianceApproveMutation, useVariancePendingMutation, useVariancePendingApproveMutation,
    useVarianceRejectMutation, useVarianceCompleteMutation, useVarianceWorklistSearchQuery, useIncidentCommentAddMutation,
    useIncidentCommentEditMutation, useIncidentCommentDeleteMutation, useIncidentWitnessAddMutation, useIncidentWitnessRemoveMutation, 
    useIncidentUnAssignUserMutation, useIncidentAssignUsersMutation,  useMyWorklistQuery, useDocumentRemoveMutation,
    useVarianceReopenMutation, useDirectorResponseAddMutation, useDirectorResponseEditMutation,
    useDirectorResponseDeleteMutation } = varianceApi;